import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import { UserContext } from '~/app';
import { providerAssociationMachine } from '~/features/employer/provider-associations/providerAssociation.machine';
import { EmployerUser } from '~/types';
import { UserStateSnapshot } from '../user/types';
import { marketContractsMachine } from './contracts/marketContracts.machine';
import { employerFacilitiesMachine } from './facility/employerFacilities.machine';
import { listingsSearchMachine } from './listing-search/listingsSearch.machine';
import { useSearchSide } from './listing-search/searchSide';
import { jobListingMachine } from './listings/job-listing/jobListing.machine';
import { msaasListingsMachine } from './listings/msaas-listing/msaasListing.machine';
import { negotiationMachine } from './negotiations/negotiation.machine';
import { employerInfoMachine } from './organization/employer-info/employerInfo.machine';
import { MarketStateContext } from './types';

/**
 * Selects Market Ref and loading state from UserContext
 */
export const selectMarketState = (state: UserStateSnapshot) => {
  return {
    loading: state.context.loading,
    marketRef: state.context.marketRef,
  };
};

/**
 * Selects refs and provider from market context
 */
export const selectMarketContext = (state: { context: MarketStateContext }) => {
  return {
    employerFacilitiesRef: state.context.employerFacilitiesRef,
    employerInfoRef: state.context.employerInfoRef,
    employerUser: state.context.employerUser,
    jobListingRef: state.context.jobListingRef,
    marketContractsRef: state.context.marketContractsRef,
    msaasListingsRef: state.context.msaasListingsRef,
    msaasListingsSearchRef: state.context.msaasListingsSearchRef,
    msaasOfferingSearchRef: state.context.msaasOfferingSearchRef,
    negotiationRef: state.context.negotiationRef,
    provider: state.context.provider,
    providerAssociationRef: state.context.providerAssociationRef,
    roleCode: state.context.roleCode,
  };
};

type ListingRefResult = {
  loading: boolean;
  jobListingRef: ActorRefFrom<typeof jobListingMachine> | undefined;
};

export const useJobListingRef = (): ListingRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const jobListingRef = useSelector(marketRef, (state) => state?.context?.jobListingRef);
  return { loading: loading ?? false, jobListingRef };
};

type NegotiationRefResult = {
  loading: boolean;
  employerUser: EmployerUser | undefined;
  negotiationRef: ActorRefFrom<typeof negotiationMachine> | undefined;
};

export const useNegotiationRef = (): NegotiationRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const negotiationRef = useSelector(marketRef, (state) => state?.context?.negotiationRef);
  const employerUser = useSelector(marketRef, (state) => state?.context?.employerUser);
  return { loading: loading ?? false, negotiationRef, employerUser };
};

type MsaasListingRefResult = {
  loading: boolean;
  msaasListingsRef: ActorRefFrom<typeof msaasListingsMachine> | undefined;
};
export const useMsaasListingRef = (): MsaasListingRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const msaasListingsRef = useSelector(marketRef, (state) => state?.context?.msaasListingsRef);
  return { loading: loading ?? false, msaasListingsRef };
};

type MsaasListingSearchRefResult = {
  loading: boolean;
  msaasListingsSearchRef: ActorRefFrom<typeof listingsSearchMachine> | undefined;
};

// Reads the search of the tab it renders under: the Buying tab (OFFERING) has its own machine.
export const useMsaasListingSearchRef = (): MsaasListingSearchRefResult => {
  const side = useSearchSide();
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const msaasListingsSearchRef = useSelector(marketRef, (state) =>
    side === 'OFFERING'
      ? state?.context?.msaasOfferingSearchRef
      : state?.context?.msaasListingsSearchRef
  );
  return { loading: loading ?? false, msaasListingsSearchRef };
};

type ProviderAssociationRefResult = {
  loading: boolean;
  employerUser: EmployerUser | undefined;
  providerAssociationRef: ActorRefFrom<typeof providerAssociationMachine> | undefined;
};

export const useProviderAssociationRef = (): ProviderAssociationRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const providerAssociationRef = useSelector(
    marketRef,
    (state) => state?.context?.providerAssociationRef
  );
  const employerUser = useSelector(marketRef, (state) => state?.context?.employerUser);
  return { loading: loading ?? false, providerAssociationRef, employerUser };
};

type MarketContractsRefResult = {
  loading: boolean;
  employerUser: EmployerUser | undefined;
  marketContractsRef: ActorRefFrom<typeof marketContractsMachine> | undefined;
};

type EmployerFacilitiesRefResult = {
  loading: boolean;
  employerFacilitiesRef: ActorRefFrom<typeof employerFacilitiesMachine> | undefined;
};

type EmployerInfoRefResult = {
  loading: boolean;
  employerInfoRef: ActorRefFrom<typeof employerInfoMachine> | undefined;
};

export const useEmployerFacilitiesRef = (): EmployerFacilitiesRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const employerFacilitiesRef = useSelector(
    marketRef,
    (state) => state?.context?.employerFacilitiesRef
  );
  return { loading: loading ?? false, employerFacilitiesRef };
};

export const useEmployerInfoRef = (): EmployerInfoRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const employerInfoRef = useSelector(marketRef, (state) => state?.context?.employerInfoRef);
  return { loading: loading ?? false, employerInfoRef };
};

export const useMarketContractsRef = (): MarketContractsRefResult => {
  const { loading, marketRef } = useSelector(UserContext.useActorRef(), selectMarketState);
  const marketContractsRef = useSelector(marketRef, (state) => state?.context?.marketContractsRef);
  const employerUser = useSelector(marketRef, (state) => state?.context?.employerUser);
  return { loading: loading ?? false, marketContractsRef, employerUser };
};
